import React, { useState, useEffect, useMemo } from "react";
import apiClient from "../../../utils/apiClient";
import server from "../../../utils/serverConfig";
import { SearchIcon, X, Info, Inbox, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import useCashCarryArbStore from "../../../contexts/LTPStore/useCashCarryArbStore";

const API = server.godseye.baseUrl.replace(/\/$/, "");

const ArbRow = React.memo(({ row, index }) => {
    const arbPct = row.arb_percentage;
    const synDiffPct = row.synthetic_vs_future_pct;
    const synDiffVal = row.synthetic_vs_future_diff;

    const pctColor = arbPct > 2 ? "text-emerald-400" : arbPct >= 0.5 ? "text-amber-400" : "text-rose-400";
    const synColor = synDiffPct > 0 ? "text-emerald-400" : synDiffPct < 0 ? "text-rose-400" : "text-zinc-400";
    const statusColor = row.status === "PREMIUM" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" : "text-amber-400 bg-amber-500/10 border-amber-500/20";

    return (
        <tr className="hover:bg-white/5 transition-colors">
            <td className="p-3 text-zinc-400 font-medium">{index + 1}</td>
            <td className="p-3 font-semibold text-emerald-400">{row.stock_symbol}</td>
            <td className="p-3 text-zinc-300">{row.future_symbol || "—"}</td>
            <td className="p-3 text-right font-medium text-white">
                ₹{row.stock_price != null ? Number(row.stock_price).toFixed(2) : "—"}
            </td>
            <td className="p-3 text-right text-zinc-300">
                ₹{row.future_price != null ? Number(row.future_price).toFixed(2) : "—"}
            </td>
            <td className={`p-3 text-right font-extrabold transition-colors duration-200 ${pctColor}`}>
                {arbPct != null ? `${Number(arbPct).toFixed(4)}%` : "—"}
            </td>
            <td className="p-3 text-zinc-300">
                {row.atm_strike != null ? (
                    <div>
                        <span className="font-semibold text-indigo-300">{row.atm_strike}</span>
                        <div className="text-[10px] text-zinc-500 truncate max-w-[150px]" title={`CE: ${row.ce_symbol || "—"} | PE: ${row.pe_symbol || "—"}`}>
                            {row.ce_symbol ? row.ce_symbol.replace("NSE:", "") : "—"} / {row.pe_symbol ? row.pe_symbol.replace("NSE:", "") : "—"}
                        </div>
                    </div>
                ) : (
                    "—"
                )}
            </td>
            <td className="p-3 text-right font-medium text-zinc-300">
                {row.ce_price != null || row.pe_price != null ? (
                    <span>
                        ₹{row.ce_price != null ? Number(row.ce_price).toFixed(2) : "—"} / ₹{row.pe_price != null ? Number(row.pe_price).toFixed(2) : "—"}
                    </span>
                ) : (
                    "—"
                )}
            </td>
            <td className="p-3 text-right text-zinc-300">
                {row.synthetic_future_price != null ? `₹${Number(row.synthetic_future_price).toFixed(2)}` : "—"}
            </td>
            <td className={`p-3 text-right font-extrabold transition-colors duration-200 ${synColor}`}>
                {synDiffPct != null ? (
                    <div title={`Formula: ((Synthetic Future - Real Future) / Real Future) × 100 | Diff: ₹${synDiffVal != null ? (synDiffVal >= 0 ? `+${synDiffVal}` : synDiffVal) : "—"} (Synthetic Future: ₹${row.synthetic_future_price != null ? Number(row.synthetic_future_price).toFixed(2) : "—"}, Future: ₹${row.future_price != null ? Number(row.future_price).toFixed(2) : "—"})`}>
                        <span>{synDiffPct >= 0 ? `+${Number(synDiffPct).toFixed(4)}%` : `${Number(synDiffPct).toFixed(4)}%`}</span>
                        <div className="text-[10px] font-medium text-zinc-400">
                            (₹{synDiffVal != null ? (synDiffVal >= 0 ? `+${synDiffVal}` : synDiffVal) : "—"})
                        </div>
                    </div>
                ) : (
                    "—"
                )}
            </td>
            <td className="p-3 text-right text-zinc-300">{row.dte != null ? `${row.dte}d` : "—"}</td>
            <td className="p-3 text-right">
                {row.status ? (
                    <span className={`px-2 py-1 rounded text-[10px] font-bold border uppercase tracking-wider ${statusColor}`}>
                        {row.status}
                    </span>
                ) : (
                    "—"
                )}
            </td>
        </tr>
    );
});
ArbRow.displayName = "ArbRow";

const CashCarryArbView = () => {
    const { arbData, setInitialData, lastUpdated } = useCashCarryArbStore();
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState(null);
    const [sortDesc, setSortDesc] = useState(true);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const res = await apiClient.get(`${API}/alpaca/cash-carry-arb`);
            if (res.data?.data) {
                setInitialData(res.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch Cash Carry ARB initial data:", error);
            toast.error("Failed to load Cash Carry ARB data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Fetch initially if store is empty
        if (arbData.length === 0) {
            fetchInitialData();
        }
    }, []);

    const handleSort = (col) => {
        if (sortBy === col) {
            if (sortDesc) {
                setSortDesc(false);
            } else {
                setSortBy(null);
            }
        } else {
            setSortBy(col);
            setSortDesc(true);
        }
    };

    const sortedAndFilteredData = useMemo(() => {
        let filtered = arbData;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(r => 
                r.stock_symbol?.toLowerCase().includes(q) || 
                r.future_symbol?.toLowerCase().includes(q) ||
                r.ce_symbol?.toLowerCase().includes(q) ||
                r.pe_symbol?.toLowerCase().includes(q)
            );
        }

        if (sortBy) {
            filtered = [...filtered].sort((a, b) => {
                let valA = a[sortBy];
                let valB = b[sortBy];

                if (valA == null) return sortDesc ? 1 : -1;
                if (valB == null) return sortDesc ? -1 : 1;

                if (typeof valA === "string") {
                    valA = valA.toLowerCase();
                    valB = (valB || "").toLowerCase();
                }

                if (valA < valB) return sortDesc ? 1 : -1;
                if (valA > valB) return sortDesc ? -1 : 1;
                return 0;
            });
        }
        return filtered;
    }, [arbData, searchQuery, sortBy, sortDesc]);

    const hasNoData = sortedAndFilteredData.length === 0;

    return (
        <div className="flex-1 flex flex-col min-h-0 bg-[#14233a] border border-[#1a2b4c] rounded-xl p-4 shadow-lg text-white">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 border-b border-white/5 pb-3">
                <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-xs font-bold uppercase tracking-wider text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-3 py-1.5 rounded-md select-none">
                        Inter Asset Arbitrage
                    </span>

                    <span className={`flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-md border select-none ${
                        arbData.length > 0
                            ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/20 animate-pulse"
                            : "text-zinc-500 bg-zinc-800/30 border-zinc-700/30"
                    }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${arbData.length > 0 ? "bg-emerald-400" : "bg-zinc-500"}`}></span>
                        {arbData.length > 0 ? "LIVE REALTIME" : "CONNECTING…"}
                    </span>

                    {sortBy ? (
                        <button
                            onClick={() => setSortBy(null)}
                            className="flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-md border border-amber-500/20 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-all select-none cursor-pointer"
                            title="Click to freeze rows and pause automatic sorting"
                        >
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500"></span>
                            </span>
                            SORT: {sortBy.replace("_", " ").toUpperCase()} ({sortDesc ? "DESC" : "ASC"})
                        </button>
                    ) : (
                        <span className="flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-md border border-sky-500/20 bg-sky-500/10 text-sky-400 select-none" title="Rows are frozen in their current order. Click any column header to sort.">
                            <span className="w-1.5 h-1.5 rounded-full bg-sky-400"></span>
                            ROWS FREEZE
                        </span>
                    )}

                    {lastUpdated && (
                        <span className="text-[10px] text-zinc-500 font-mono">
                            Last tick: {new Date(lastUpdated).toLocaleTimeString()}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative">
                        <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input type="text"
                            placeholder="Search symbol…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-[#22272b] border border-transparent focus:border-zinc-650 rounded-lg pl-9 pr-8 py-1.5 text-xs text-[#b6c2cf] focus:outline-none transition-all placeholder:text-zinc-500 w-64 shadow-inner"
                        />
                        {searchQuery && (
                            <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    <button onClick={fetchInitialData} disabled={loading}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-zinc-300 hover:text-white transition-all disabled:opacity-50">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        {loading ? "Refreshing…" : "Refresh"}
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 flex flex-col min-h-0 pr-1">
                {loading && arbData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3">
                        <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                        <span className="text-xs text-zinc-500 font-bold uppercase tracking-widest">Loading…</span>
                    </div>
                ) : hasNoData ? (
                    <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
                        <Inbox className="w-12 h-12 text-zinc-700 mb-3" />
                        <p className="text-sm font-semibold">No Arbitrage Data Available</p>
                        <p className="text-xs text-zinc-600 mt-1">Wait for websocket updates or click refresh.</p>
                    </div>
                ) : (
                    <div className="flex-1 overflow-auto rounded-lg border border-white/5 bg-[#0d0f18]/60 custom-scroll">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-[#14233a] border-b border-white/10 text-zinc-400 text-xs font-semibold select-none">
                                    {[
                                        { label: "#", tip: "Row number.", align: "left", key: null },
                                        { label: "Stock Symbol", tip: "Equity stock ticker.", align: "left", key: "stock_symbol" },
                                        { label: "Future Symbol", tip: "Futures contract ticker.", align: "left", key: "future_symbol" },
                                        { label: "Stock Price", tip: "Current stock price.", align: "right", key: "stock_price" },
                                        { label: "Future Price", tip: "Current futures price.", align: "right", key: "future_price" },
                                        { label: "Arb %", tip: "Arbitrage Percentage between future and cash.", align: "right", key: "arb_percentage" },
                                        { label: "Options Symbol", tip: "ATM Strike & CE/PE Symbols.", align: "left", key: "atm_strike" },
                                        { label: "Call / Put LTP", tip: "Call Option Price (CE) / Put Option Price (PE).", align: "right", key: "ce_price" },
                                        { label: "Synthetic Future", tip: "Synthetic Future Price = ATM Strike + Call LTP - Put LTP.", align: "right", key: "synthetic_future_price" },
                                        { label: "Syn vs Fut %", tip: "Formula: ((Synthetic Future - Real Future) / Real Future) × 100", align: "right", key: "synthetic_vs_future_pct" },
                                        { label: "DTE", tip: "Days to Expiration.", align: "right", key: "dte" },
                                        { label: "Status", tip: "Premium or Discount.", align: "right", key: "status" },
                                    ].map(({ label, tip, align, key }) => (
                                        <th key={label}
                                            onClick={key ? () => handleSort(key) : undefined}
                                            className={`p-3 whitespace-nowrap sticky top-0 z-10 bg-[#14233a] ${key ? "cursor-pointer hover:text-white" : ""} ${align === "right" ? "text-right" : "text-left"}`}
                                        >
                                            <div className={`flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
                                                {label}
                                                {key && sortBy === key && (sortDesc ? " ↓" : " ↑")}
                                                <div className="group relative inline-block cursor-help">
                                                    <Info className="w-3.5 h-3.5 text-zinc-500 hover:text-zinc-300" />
                                                    <div className={`absolute top-full ${align === "right" ? "right-0" : "left-0"} mt-2 hidden group-hover:block w-48 bg-[#090b11]/95 border border-white/10 text-zinc-300 text-[10px] p-2 rounded-lg shadow-xl whitespace-normal normal-case font-normal leading-normal z-50`}>
                                                        {tip}
                                                    </div>
                                                </div>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5 text-xs text-zinc-300">
                                {sortedAndFilteredData.map((row, i) => <ArbRow key={row.stock_symbol} row={row} index={i} />)}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CashCarryArbView;

